import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedUsers, IListUser, UserStatus } from 'src/redux/slices/users';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import { AuditAction, AuditType, IAuditTableFilters, IAuditTableFilterValue } from 'src/types/audit';

type Props = {
  filters: IAuditTableFilters;
  onFilters: (name: string, value: IAuditTableFilterValue) => void;
  handleResetFilters: VoidFunction;
  canReset: boolean;
};

export default function AuditLogTableToolbar({
  filters,
  onFilters,
  handleResetFilters,
  canReset,
}: Props) {
  const dispatch = useAppDispatcher();

  const { users } = useAppSelector((state) => state.users);

  const [input, setInput] = useState<string>('');

  const [selectedActor, setSelectedActor] = useState<IListUser | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilters('generalSearch', value);
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedActorSearch = useCallback(
    debounce((value: string) => {
      dispatch(
        getPaginatedUsers({
          page: 0,
          limit: 25,
          firstName: value,
          email: value,
          lastName: value,
          permissions: [],
          sort: 'asc',
          sortBy: 'firstName',
          status: [UserStatus.ACTIVE, UserStatus.DISABLED],
        })
      );
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    dispatch(
      getPaginatedUsers({
        page: 0,
        limit: 25,
        firstName: '',
        email: '',
        lastName: '',
        permissions: [],
        sort: 'asc',
        sortBy: 'firstName',
        status: [UserStatus.ACTIVE, UserStatus.DISABLED],
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filters.actorId) {
      setSelectedActor(null);
    }
  }, [filters.actorId]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedActorSearch.cancel();
    };
  }, [debouncedSearch, debouncedActorSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value);
  };

  const handleFilterType = useCallback(
    (event: SelectChangeEvent<string>) => {
      onFilters('type', event.target.value);
    },
    [onFilters]
  );

  const handleFilterAction = useCallback(
    (event: SelectChangeEvent<string>) => {
      onFilters('action', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStartDate = useCallback(
    (date: any) => {
      onFilters('startDate', date);
    },
    [onFilters]
  );

  const handleFilterEndDate = useCallback(
    (date: any) => {
      onFilters('endDate', date);
    },
    [onFilters]
  );

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    setSelectedActor(null);
    handleResetFilters();
  }, [handleResetFilters]);

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      flexWrap="wrap"
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <TextField
          fullWidth
          value={input}
          onChange={handleSearchChange}
          placeholder="Search by actor email..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment:
              input.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearchText}>
                    <Iconify icon="eva:close-fill" color="text.disabled" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          inputProps={{
            autoComplete: 'off',
          }}
        />
      </Stack>

      <FormControl
        sx={{
          flexShrink: 0,
          width: { xs: 1, md: 180 },
        }}
      >
        <InputLabel>Type</InputLabel>

        <Select
          value={filters.type}
          onChange={handleFilterType}
          input={<OutlinedInput label="Type" />}
          renderValue={(selected) => (
            <Label variant="soft" color="primary">
              {selected}
            </Label>
          )}
        >
          {Object.values(AuditType).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        sx={{
          flexShrink: 0,
          width: { xs: 1, md: 180 },
        }}
      >
        <InputLabel>Action</InputLabel>

        <Select
          value={filters.action}
          onChange={handleFilterAction}
          input={<OutlinedInput label="Action" />}
          renderValue={(selected) => (
            <Label variant="soft" color="primary">
              {selected}
            </Label>
          )}
        >
          {Object.values(AuditAction).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Autocomplete
        fullWidth
        sx={{ flexShrink: 0, width: { xs: 1, md: 240 } }}
        options={users.users}
        value={selectedActor}
        onChange={(event, value) => {
          setSelectedActor(value);
          onFilters('actorId', value?._id ?? '');
        }}
        onInputChange={(event, value, reason) => {
          if (reason === 'input') {
            debouncedActorSearch(value);
          }
        }}
        getOptionLabel={(option: IListUser) =>
          `${option.firstName}${option.lastName ? ` ${option.lastName}` : ''} (${option.email})`
        }
        isOptionEqualToValue={(option, value) => option._id === value._id}
        renderInput={(params) => <TextField {...params} label="Actor" />}
      />

      <CustomDatePicker
        disableFuture
        label="Start Date"
        value={filters.startDate}
        onChange={handleFilterStartDate}
      />
      <CustomDatePicker
        disableFuture
        label="End Date"
        value={filters.endDate}
        onChange={handleFilterEndDate}
      />

      {canReset && (
        <Button
          color="error"
          onClick={clearAll}
          variant="text"
          sx={{ textDecoration: 'underline' }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
